import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  MapPin, Clock, Navigation, ArrowLeft, Utensils, Mountain,
  Building2, Star, Users, Check, ArrowRight, AlertCircle, Plus, X,
  Car, Plane, Ship, Footprints, Eye, CalendarDays, Sparkles, MessageCircle, Sun,
  ChevronLeft, ChevronRight, Images,
} from 'lucide-react';
import { gatewayFor, namedPlaceFor, haversineKm, classifyFeature, featureFromLocationType, roadVia, mapCanvasSize } from './itinerary/util';
import { TRACK_PAGE_VIEW } from '../graphql/analytics';
import { getVisitorSessionId } from '../lib/visitorSession';

const RouteMap = lazy(() => import('./itinerary/RouteMap'));

/* ─── Design tokens ─── */
const G  = '#B8975A';
const GD = 'rgba(184,151,90,0.13)';
const N  = '#080e1a';
const CR = '#F5F3EF';
const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`;

/* ─── GraphQL ─── */
const GET_TOUR_ITINERARY = gql`
  query GetTourItinerary($slug: String!) {
    tour(slug: $slug) {
      id name slug tagline featuredImage
      durationLabel
      duration: durationDays
      difficulty: difficultyLevel
      maxParticipants
      includes
      excludes
      mainActivities
      recommendedBestSeason
      arrivalAirportName
      pullQuote pullQuoteBy
      parks { name country { name } }
      upcomingDepartures { id departureDate returnDate spotsRemaining isFull status }
      additionalCosts {
        id name description note icon
        costType priceDisplay pricePer sortOrder
      }
      itineraryDays {
        id dayNumber title category
        location { name latitude longitude locationType country { name } }
        primaryPark {
          name slug latitude longitude country { name } areaSqKm
          featuredImage galleryImages elevationM mainAttractions
          bestMonths peakSeasonMonths rainySeasonMonths hasMigrationZones
        }
        accommodation {
          id name featuredImage galleryImages shortDescription description level levelLabel
          locationType averageRating
          propertyType { name }
        }
        roomType { name }
        boardBasis { name }
        description insiderTip
        timeLabel durationLabel locationLabel
        details includesAtStop
        driveFromPreviousKm isOvernight craterDescent
      }
    }
  }
`;

/* Where the Great Migration herds usually are, month by month — the same
   calendar the builder and the suggested routes use (migration_calendar). */
const GREAT_MIGRATION = gql`
  query ItineraryGreatMigration {
    greatMigration { month where moment zones { key label strength } }
  }
`;

const MIGRATION_PARKS = new Set(['serengeti-national-park', 'maasai-mara-national-reserve']);
// What a day's own words say about where in the park it is → calendar zone.
const ZONE_WORDS = {
  ndutu: ['ndutu', 'southern serengeti', 'south'], southern: ['southern', 'central', 'seronera'],
  seronera: ['seronera', 'central'], grumeti: ['grumeti', 'western', 'west'],
  kogatende: ['kogatende', 'lamai', 'northern', 'north'], northern: ['northern', 'north', 'kogatende', 'lamai'],
  eastern: ['eastern', 'lobo'],
};

const LOC_TYPE_LABELS = {
  inside_park:  'Inside the park',
  in_park:      'Inside the park',
  nearby:       'Near the park',
  near_park:    'Near the park',
  outside_park: 'Outside the park',
  gateway_town: 'Gateway town',
  city:         'City',
  coastal:      'Coastal',
};

/* ─── Cost type map ─── */
const COST_TYPE_MAP = {
  optional: { cls: 'opt',  label: 'Optional' },
  required: { cls: 'req',  label: 'Required' },
  personal: { cls: 'pers', label: 'Personal' },
};

/* ─────────────────────────────────────────
   Turn itinerary days into route "stops":
   resolve a coordinate for each day (park →
   location → gateway city), collapse runs at
   the same place, tag start/end/major/minor
   and the transport used to reach each one.
───────────────────────────────────────── */
const FLIGHT_KM = 450;

function buildStops(days, arrivalName) {
  const pts = days.map((d, idx) => {
    // A named, specific place (a trek's fixed camps, a named concession)
    // always wins over the coarser park-level centroid below — otherwise
    // every day sharing one primaryPark collapses onto the exact same dot
    // and the drawn route shows only "the main destination", never the
    // itinerary's real day-by-day movement through it.
    // locationLabel is checked alone first — it names where the day
    // actually is. title often narrates the day's whole journey ("Karanga
    // → Barafu Summit Camp"), so matching against it can pick up the
    // *previous* camp mentioned in passing; only fall back to it when
    // locationLabel itself doesn't name a known place.
    const np = namedPlaceFor(d.locationLabel) || namedPlaceFor(d.title);

    let lat = np ? np.ll[0] : parseFloat(d.primaryPark?.latitude);
    let lng = np ? np.ll[1] : parseFloat(d.primaryPark?.longitude);
    let gwName = null, gwCountry = null, gwFeature = null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      lat = parseFloat(d.location?.latitude);
      lng = parseFloat(d.location?.longitude);
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      const g = gatewayFor(d.locationLabel, d.title, d.location?.name);
      if (g) { lat = g.ll[0]; lng = g.ll[1]; gwName = g.name; gwCountry = g.country; gwFeature = g.feature; }
    }
    const name =
      np?.name ||
      d.primaryPark?.name ||
      d.location?.name ||
      gwName ||
      (d.locationLabel || '').split(',')[0].trim() ||
      d.title;
    const country = np?.country || d.primaryPark?.country?.name || d.location?.country?.name || gwCountry || null;
    const feature =
      np?.feature ||
      (d.primaryPark && classifyFeature(d.primaryPark.name)) ||
      (d.location && (featureFromLocationType(d.location.locationType) || classifyFeature(d.location.name))) ||
      gwFeature ||
      classifyFeature(name);
    const ok = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    // The park's own extent, for the soft "protected area" shading on the
    // backdrop — tied to the PARK's real centroid, not to whatever more
    // specific point (a named camp, a gateway) this day actually resolved
    // to above, so a multi-camp trek doesn't draw the same park-sized blob
    // centered on every single camp.
    const parkArea = parseFloat(d.primaryPark?.areaSqKm);
    const parkZone = d.primaryPark && Number.isFinite(parkArea) && parkArea > 0
      ? {
          name: d.primaryPark.name,
          lat: parseFloat(d.primaryPark.latitude),
          lng: parseFloat(d.primaryPark.longitude),
          areaSqKm: parkArea,
        }
      : null;
    return {
      idx, day: d.dayNumber, name, country, feature, isTrek: !!np?.isTrek, parkZone, park: d.primaryPark?.name || null,
      lat: ok ? lat : null, lng: ok ? lng : null, overnight: !!d.isOvernight,
    };
  }).filter((p) => p.lat != null);

  if (!pts.length) return { stops: [], parkZones: [] };

  const parkZones = [...new Map(
    pts.filter((p) => p.parkZone).map((p) => [p.parkZone.name, p.parkZone])
  ).values()];

  const stops = [];
  /* Where the safari really starts: guests fly into the arrival airport and
     drive to the first park on day 1 — without this the route began at the
     park, as if they'd been dropped there. Never active (lastDayIdx < 0);
     tapping it opens day 1. */
  const gw = arrivalName ? gatewayFor(arrivalName) : null;
  // Only when day 1 drives somewhere real from the airport (Kilimanjaro →
  // Tarangire, ~120 km). A first night in the gateway town itself (Arusha,
  // 45 km) is just the airport transfer — a second marker there only
  // crowds the map.
  if (gw && haversineKm(gw.ll, [pts[0].lat, pts[0].lng]) > 60) {
    stops.push({
      key: 'arrive', name: arrivalName.replace(/ International/, '').replace(/ Airport$/, ' Airport'),
      lat: gw.ll[0], lng: gw.ll[1], country: gw.country, feature: 'airport', isTrek: false,
      dayFrom: 1, dayTo: 1, firstDayIdx: 0, lastDayIdx: -1, overnight: false, isArrival: true,
    });
  }
  for (const p of pts) {
    const prev = stops[stops.length - 1];
    // Named trek waypoints (e.g. Kilimanjaro's camps) sit deliberately
    // close together — a day's hiking distance apart, often well under
    // the 8km "probably the same place" fallback below — so that fallback
    // must never fire between two of them, or the trek route collapses
    // right back onto one dot.
    const near = prev && (prev.name === p.name ||
      (!prev.isTrek && !p.isTrek && haversineKm([prev.lat, prev.lng], [p.lat, p.lng]) < 8));
    if (near) {
      prev.dayTo = p.day;
      prev.lastDayIdx = p.idx;
      prev.overnight = prev.overnight || p.overnight;
    } else {
      stops.push({
        key: `${p.name}-${p.day}`, name: p.name, lat: p.lat, lng: p.lng,
        country: p.country, feature: p.feature, isTrek: p.isTrek, park: p.park,
        dayFrom: p.day, dayTo: p.day, firstDayIdx: p.idx, lastDayIdx: p.idx,
        overnight: p.overnight,
      });
    }
  }

  const finalStops = stops.map((s, i) => {
    let kind = 'major';
    if (i === 0) kind = 'start';
    else if (i === stops.length - 1) kind = 'end';
    else if (s.dayFrom === s.dayTo && !s.overnight) kind = 'minor';

    let transportIn = null;
    if (i > 0) {
      const prev = stops[i - 1];
      const km = haversineKm([prev.lat, prev.lng], [s.lat, s.lng]);
      const txt = `${prev.name} ${s.name}`.toLowerCase();
      const islandHop = /zanzibar|pemba|mafia|island/.test(txt)
        || prev.feature === 'island' || s.feature === 'island';
      const coastalHop = km < 130 && (islandHop || /ferry|dhow|mombasa|dar es salaam|stone town/.test(txt));
      // Different country on a curated safari almost always means a
      // scheduled or charter flight between park airstrips — overland
      // border crossings with a safari vehicle are the exception, not the
      // rule (e.g. Serengeti ↔ Maasai Mara is ~120km but crosses the
      // Kenya/Tanzania border, so operators fly it even though it's well
      // under the generic distance threshold below).
      const crossBorder = !!prev.country && !!s.country && prev.country !== s.country;
      if (prev.isTrek && s.isTrek) transportIn = 'trek';
      else if (coastalHop) transportIn = 'boat';
      else if (crossBorder) transportIn = 'flight';
      else if (km > FLIGHT_KM || (islandHop && km > 150)) transportIn = 'flight';
      else transportIn = 'road';
    }
    // Road legs follow the real roads (and name the towns on the way).
    const via = transportIn === 'road' && i > 0
      ? roadVia([stops[i - 1].lat, stops[i - 1].lng], [s.lat, s.lng])
      : null;
    return { ...s, kind, transportIn, via };
  });

  return { stops: finalStops, parkZones };
}

/* ─────────────────────────────────────────
   Seasons — what a month is like in a park,
   from the park's own best / peak / rainy
   months. Worded the way a guide would say
   it: green season is a trade-off, not a
   warning.
───────────────────────────────────────── */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function parseList(v) {
  if (Array.isArray(v)) return v;
  try { const x = JSON.parse(v || '[]'); return Array.isArray(x) ? x : []; } catch { return []; }
}

const SEASON = {
  peak:     { label: 'Peak season',        tone: '#B8975A', note: 'Prime game viewing — the best camps fill early.' },
  best:     { label: 'Great time to go',   tone: '#5f8a5a', note: 'Dry, open bush and wildlife gathered near water.' },
  green:    { label: 'Green season',       tone: '#4f8a8b', note: 'Lush landscapes, newborn animals and far fewer vehicles.' },
  shoulder: { label: 'Shoulder season',    tone: '#8a7f70', note: 'Quieter parks and good value.' },
};

function seasonFor(park, month) {
  if (!park) return null;
  const peak = parseList(park.peakSeasonMonths), best = parseList(park.bestMonths), rainy = parseList(park.rainySeasonMonths);
  if (!peak.length && !best.length && !rainy.length) return null;
  if (peak.includes(month)) return SEASON.peak;
  if (best.includes(month)) return SEASON.best;
  if (rainy.includes(month)) return SEASON.green;
  return SEASON.shoulder;
}

/* Does this day meet the herds? The Maasai Mara does whenever the herds are
   there; a Serengeti day when its own words place it in a zone the herds
   are in that month. */
function herdsForDay(monthRow, parkSlug, text) {
  if (!monthRow || !MIGRATION_PARKS.has(parkSlug)) return null;
  const keys = monthRow.zones.map((z) => z.key);
  if (parkSlug === 'maasai-mara-national-reserve') return { where: monthRow.where, moment: monthRow.moment, here: keys.includes('mara') };
  const t = (text || '').toLowerCase();
  const here = keys.some((k) => k !== 'mara' && (ZONE_WORDS[k] || []).some((w) => t.includes(w)));
  return { where: monthRow.where, moment: monthRow.moment, here };
}

/* The month picker is calendar-scoped: the next twelve months from today,
   in order, each with its real year — "Jan" always means the coming
   January, and a month that has already gone can't be picked. */
function upcomingMonths() {
  const now = new Date();
  return Array.from({ length: 12 }, (_, k) => {
    const d = new Date(now.getFullYear(), now.getMonth() + k, 1);
    const y = d.getFullYear(), m = d.getMonth() + 1;
    return { y, m, key: `${y}-${String(m).padStart(2, '0')}` };
  });
}
const monthKeyOf = (iso) => (iso || '').slice(0, 7);   // 'YYYY-MM' — no timezone shift

function defaultMonth(tour, window) {
  const keys = new Set(window.map((w) => w.key));
  // The next scheduled departure, when it falls in the window …
  const next = (tour?.upcomingDepartures || []).map((d) => monthKeyOf(d.departureDate)).find((k) => keys.has(k));
  if (next) return next;
  // … else the next time the best season comes round …
  const m = (tour?.recommendedBestSeason || '').slice(0, 3);
  const i = MONTHS.findIndex((x) => x.toLowerCase() === m.toLowerCase());
  const best = i >= 0 ? window.find((w) => w.m === i + 1) : null;
  // … else this month.
  return (best || window[0]).key;
}

/* Safari roads: ~40 km/h door to door, game-viewing on the way included. */
function driveTime(km) {
  const h = Math.round((Number(km) / 40) * 2) / 2;
  if (!h) return null;
  const whole = Math.floor(h);
  return `~${whole || ''}${h % 1 ? '½' : ''} hr${h > 1 ? 's' : ''}`;
}

// Date-only strings parsed as local midnight: new Date('2026-10-26') is UTC
// midnight, which shows as the 25th anywhere west of Greenwich.
const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const pad2 = (n) => String(n).padStart(2, '0');

const TRANSPORT = {
  road:   { I: Car,        verb: 'By road' },
  flight: { I: Plane,      verb: 'By light aircraft' },
  boat:   { I: Ship,       verb: 'By boat' },
  trek:   { I: Footprints, verb: 'On foot' },
};

/* ─────────────────────────────────────────
   Dense groups → one marker + an inset.
   A Kilimanjaro trek's seven camps sit a few
   km apart; on a 20-day, two-country map
   they collapse into a knot whose labels get
   shoved far from their markers. A run of 3+
   consecutive stops within ~60 km of each
   other (and small next to the trip) is one
   marker ("Kilimanjaro trek · Days 2–8") and
   mapped again, zoomed in, as an inset.
───────────────────────────────────────── */
function spanKm(list) {
  let best = 0;
  for (let a = 0; a < list.length; a++) for (let b = a + 1; b < list.length; b++) {
    best = Math.max(best, haversineKm([list[a].lat, list[a].lng], [list[b].lat, list[b].lng]));
  }
  return best;
}

function groupDense(stops, parkZones) {
  if (stops.length < 5) return { mapStops: stops, insets: [] };
  // Small relative to the trip, and small in absolute terms: trek camps
  // (~25 km across) or a Zanzibar loop fold; whole parks never do.
  const limit = Math.min(60, Math.max(20, spanKm(stops) * 0.06));
  const out = [], insets = [];
  let i = 0;
  while (i < stops.length) {
    let j = i;
    // Never fold the trip's start or end — they are the map's anchors.
    if (stops[i].kind !== 'start' && stops[i].kind !== 'end') {
      while (j + 1 < stops.length && stops[j + 1].kind !== 'start' && stops[j + 1].kind !== 'end'
        && spanKm(stops.slice(i, j + 2)) <= limit) j += 1;
    }
    const run = stops.slice(i, j + 1);
    if (run.length >= 3) {
      const first = run[0], last = run[run.length - 1];
      const parkShort = (first.park || '').replace(/ National Park| Conservation Area| Game Reserve/, '');
      const title = first.isTrek ? `${parkShort || first.name} trek` : (parkShort || first.name);
      const days = first.dayFrom === last.dayTo ? `Day ${first.dayFrom}` : `Days ${first.dayFrom}–${last.dayTo}`;
      out.push({
        ...first, key: `group-${first.key}`, name: title,
        lat: run.reduce((t, x) => t + x.lat, 0) / run.length,
        lng: run.reduce((t, x) => t + x.lng, 0) / run.length,
        feature: first.isTrek ? 'mountain' : first.feature,
        dayTo: last.dayTo, lastDayIdx: last.lastDayIdx, overnight: true, kind: 'major', isGroup: true,
      });
      insets.push({
        key: first.key, title, days, stops: run.map((x) => ({ ...x, via: null })),
        parkZones: parkZones.filter((z) => z.name === first.park),
      });
    } else {
      out.push(...run);
    }
    i = j + 1;
  }
  return { mapStops: out, insets };
}

/* ─── Styles ─── */
const S = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

    *,*::before,*::after{box-sizing:border-box}
    html,body{overflow-x:clip}
    .fc{font-family:'Cormorant Garamond',serif}
    .fj{font-family:'Jost',sans-serif}

    @keyframes riseIn  {from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn  {from{opacity:0}to{opacity:1}}
    @keyframes shimmer {0%{background-position:200% 0}100%{background-position:-200% 0}}
    .ri{animation:riseIn .9s cubic-bezier(.22,1,.36,1) both}
    .fi{animation:fadeIn .7s ease both}
    .d1{animation-delay:.1s}.d2{animation-delay:.22s}.d3{animation-delay:.36s}.d4{animation-delay:.5s}

    .sl{display:flex;align-items:center;gap:10px;margin-bottom:10px}
    .sl-line{width:20px;height:1px;background:${G};flex-shrink:0}
    .sl-text{font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.2em;text-transform:uppercase;color:${G}}
    .sl-light .sl-text{color:rgba(184,151,90,.75)}
    .sl-light .sl-line{background:rgba(184,151,90,.5)}
    .skeleton{background:linear-gradient(90deg,#ede9e3 25%,#e5e1db 50%,#ede9e3 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;border-radius:2px}

    /* Hero */
    .itin-hero{position:relative;background:${N};padding-top:7rem;padding-bottom:3.5rem;overflow:hidden}
    .itin-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.32}
    .itin-hero-grad{position:absolute;inset:0;background:linear-gradient(100deg,rgba(8,14,26,.97) 30%,rgba(8,14,26,.5) 100%)}
    .itin-hero-grain{position:absolute;inset:0;background-image:${GRAIN};opacity:.55;pointer-events:none}
    .hero-fact{display:flex;flex-direction:column;gap:4px;padding-right:28px;margin-right:28px;border-right:1px solid rgba(255,255,255,.1)}
    .hero-fact:last-child{border-right:none}

    /* Journey ribbon — the trip as stops, sized by days */
    .ribbon{display:flex;gap:3px;margin-top:36px;overflow-x:auto;scrollbar-width:none}
    .ribbon::-webkit-scrollbar{display:none}
    .ribbon-seg{min-width:120px;text-align:left;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-top:2px solid rgba(184,151,90,.35);padding:12px 14px;cursor:pointer;transition:all .25s;color:#fff}
    .ribbon-seg:hover{background:rgba(184,151,90,.1)}
    .ribbon-seg.on{background:rgba(184,151,90,.16);border-top-color:${G}}

    /* Month lens */
    .lens{background:#fff;border-bottom:1px solid #ece7de;position:sticky;top:0;z-index:30}
    .lens-inner{max-width:1500px;margin:0 auto;padding:14px clamp(16px,3vw,40px);display:flex;align-items:center;gap:18px;flex-wrap:wrap}
    .lens-months{position:relative;display:flex;gap:4px;overflow-x:auto;scrollbar-width:none;flex:1;min-width:0;scroll-behavior:smooth}
    .lens-months::-webkit-scrollbar{display:none}
    .m-pill{flex-shrink:0;font-family:'Jost',sans-serif;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;padding:8px 11px;border:1px solid #e8e4de;background:#fff;color:#12100e;cursor:pointer;transition:all .2s;position:relative}
    .m-pill:hover{border-color:${G}}
    .m-pill.on{background:${N};border-color:${N};color:#fff}
    .m-pill .m-yr{font-size:10.5px;opacity:.6;letter-spacing:.06em}
    .m-pill .dep{position:absolute;top:3px;right:3px;width:5px;height:5px;border-radius:50%;background:${G}}
    @media(max-width:760px){
      .lens-inner{gap:10px;padding-top:10px;padding-bottom:10px}
      .lens-months{flex-basis:100%}
      .lens-hint{display:none}
    }
    /* Phone: the booking actions live in the sticky aside on desktop —
       here a slim bar keeps them (and where you are) one tap away. */
    .mbar{position:fixed;left:0;right:0;bottom:0;z-index:40;display:flex;align-items:center;gap:12px;padding:10px 16px calc(10px + env(safe-area-inset-bottom));background:rgba(8,14,26,.96);backdrop-filter:blur(10px);border-top:1px solid rgba(184,151,90,.3)}
    @media(min-width:1100px){.mbar{display:none}}
    @media(max-width:1099px){.itin-page{padding-bottom:72px}.float-cluster{bottom:88px!important}}

    /* Layout */
    .itin-layout{max-width:1500px;margin:0 auto;padding:40px clamp(16px,3vw,40px) 80px;display:grid;grid-template-columns:1fr;gap:32px;align-items:start}
    @media(min-width:1100px){.itin-layout{grid-template-columns:minmax(0,1fr) 360px;gap:48px}}
    .aside{display:none}
    @media(min-width:1100px){.aside{display:flex;flex-direction:column;gap:16px;position:sticky;top:76px;max-height:calc(100vh - 92px);overflow-y:auto;scrollbar-width:none}}
    .aside::-webkit-scrollbar{display:none}

    /* Day chapters */
    .chapter{position:relative;background:#fff;border:1px solid #e8e4de;margin-bottom:22px;transition:border-color .35s,box-shadow .35s;scroll-margin-top:84px}
    .chapter.on{border-color:rgba(184,151,90,.55);box-shadow:0 10px 50px rgba(8,14,26,.06)}
    .ch-banner{position:relative;height:clamp(200px,26vw,300px);overflow:hidden;background:${N}}
    .ch-banner img{width:100%;height:100%;object-fit:cover;opacity:.85;transition:transform 1.4s cubic-bezier(.22,1,.36,1)}
    .chapter.on .ch-banner img{transform:scale(1.03)}
    .ch-banner::after{content:'';position:absolute;inset:0;background:linear-gradient(180deg,rgba(8,14,26,.05) 30%,rgba(8,14,26,.85) 100%)}
    .ch-banner-text{position:absolute;left:24px;right:24px;bottom:18px;z-index:2;color:#fff}
    .ch-num{font-family:'Cormorant Garamond',serif;font-weight:300;line-height:.8;color:${G}}
    .ch-head{padding:22px 24px 0;display:flex;gap:18px;align-items:flex-start}
    .ch-body{padding:18px 24px 24px}
    .chips{display:flex;flex-wrap:wrap;gap:6px}
    .chip{display:inline-flex;align-items:center;gap:6px;font-family:'Jost',sans-serif;font-size:12.5px;font-weight:500;padding:5px 10px;border:1px solid #ece7de;background:#faf8f4;color:#12100e}
    .flow{position:relative;margin:4px 0 0;padding-left:22px}
    .flow::before{content:'';position:absolute;left:5px;top:8px;bottom:8px;width:1px;background:linear-gradient(to bottom,${G},rgba(184,151,90,.1))}
    .flow-step{position:relative;padding:6px 0;font-family:'Jost',sans-serif;font-size:15px;font-weight:300;line-height:1.6;color:#12100e}
    .flow-step::before{content:'';position:absolute;left:-21px;top:13px;width:9px;height:9px;border:1px solid ${G};background:#fff;transform:rotate(45deg)}
    .label{font-family:'Jost',sans-serif;font-size:12px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:#12100e;margin-bottom:10px}
    .lookout{display:flex;flex-wrap:wrap;gap:6px}
    .lookout span{font-family:'Jost',sans-serif;font-size:13px;padding:5px 11px;background:${GD};border:1px solid rgba(184,151,90,.25);color:#3d3326}
    .season-box{display:flex;gap:12px;align-items:flex-start;padding:12px 14px;border:1px solid #ece7de;background:#fcfbf8}
    .guide-note{position:relative;padding:16px 18px 16px 20px;background:#fbf6ea;border:1px solid rgba(184,151,90,.25)}
    .guide-note::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:${G}}
    .transit{display:flex;align-items:center;gap:10px;margin:-8px 0 14px 26px;font-family:'Jost',sans-serif;font-size:12.5px;color:#7a7266;letter-spacing:.04em}
    .transit-line{width:1px;height:22px;background:repeating-linear-gradient(to bottom,${G} 0 3px,transparent 3px 7px)}


    /* Lodge gallery */
    .lg{display:grid;gap:4px}
    .lg.has-thumbs{grid-template-columns:2fr 1fr}
    @media(max-width:640px){.lg.has-thumbs{grid-template-columns:1fr}}
    .lg-main{position:relative;display:block;width:100%;aspect-ratio:16/10;overflow:hidden;border:none;padding:0;cursor:zoom-in;background:${N}}
    .lg-main img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .8s cubic-bezier(.22,1,.36,1)}
    .lg-main:hover img{transform:scale(1.03)}
    .lg-count{position:absolute;right:12px;bottom:12px;display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:500;color:#fff;background:rgba(8,14,26,.72);padding:6px 10px;backdrop-filter:blur(4px)}
    .lg-note{position:absolute;left:12px;bottom:12px;font-size:11.5px;letter-spacing:.06em;color:rgba(255,255,255,.85);background:rgba(8,14,26,.55);padding:5px 9px}
    /* Thumbnails stack beside the big photo and share its height. */
    .lg-thumbs{display:grid;gap:4px;grid-auto-rows:1fr}
    @media(max-width:640px){.lg-thumbs{grid-auto-flow:column;grid-auto-columns:1fr;grid-auto-rows:auto}}
    .lg-thumb{position:relative;min-height:0;overflow:hidden;border:none;padding:0;cursor:zoom-in;background:#ece7de}
    @media(max-width:640px){.lg-thumb{aspect-ratio:4/3}}
    .lg-thumb img{width:100%;height:100%;object-fit:cover;display:block}
    .lg-more{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(8,14,26,.55);color:#fff;font-size:26px}
    .stay{border:1px solid #e8e4de;background:#fff}
    .stay-head{padding:16px 18px 14px}
    .lb{position:fixed;inset:0;z-index:10000;background:rgba(5,9,17,.94);display:flex;align-items:center;justify-content:center;padding:56px 16px 72px}
    .lb img{max-width:100%;max-height:100%;object-fit:contain;box-shadow:0 20px 80px rgba(0,0,0,.5)}
    .lb-nav{position:absolute;top:50%;transform:translateY(-50%);width:46px;height:46px;display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.06);color:#fff;cursor:pointer}
    .lb-nav:hover{background:${G};color:${N};border-color:${G}}
    .lb-close{position:absolute;top:14px;right:14px;width:42px;height:42px;display:flex;align-items:center;justify-content:center;border:none;background:transparent;color:#fff;cursor:pointer}
    .lb-bar{position:absolute;left:0;right:0;bottom:0;display:flex;justify-content:space-between;gap:16px;padding:16px 20px;color:rgba(255,255,255,.75);font-size:13.5px;letter-spacing:.04em}

    /* Aside cards */
    .card{background:#fff;border:1px solid #e8e4de}
    .now{padding:16px 18px;background:${N};color:#fff}
    .dep-row{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;padding:11px 0;border-bottom:1px solid #f2eee8;background:none;border-left:none;border-right:none;border-top:none;cursor:pointer;text-align:left}
    .dep-row:last-child{border-bottom:none}

    /* Included */
    .incl-grid{display:grid;grid-template-columns:1fr;gap:20px}
    @media(min-width:760px){.incl-grid{grid-template-columns:1fr 1fr}}
    .incl-item{display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #f5f2ee;font-family:'Jost',sans-serif;font-size:15px;font-weight:300;color:#12100e}
    .incl-item:last-child{border-bottom:none}
    .addl-grid{display:grid;grid-template-columns:1fr;gap:12px}
    @media(min-width:760px){.addl-grid{grid-template-columns:1fr 1fr}}
    @media(min-width:1280px){.addl-grid{grid-template-columns:1fr 1fr 1fr}}
    .addl-badge{display:inline-flex;align-items:center;gap:4px;font-family:'Jost',sans-serif;font-size:11.5px;font-weight:600;padding:2px 8px;letter-spacing:.1em}
    .addl-badge.opt{background:rgba(184,151,90,.08);color:${G};border:1px solid rgba(184,151,90,.25)}
    .addl-badge.req{background:rgba(59,130,246,.07);color:#3b82f6;border:1px solid rgba(59,130,246,.2)}
    .addl-badge.pers{background:rgba(148,163,184,.07);color:#12100e;border:1px solid rgba(148,163,184,.2)}

    .rbtn{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:10px;font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;padding:15px 26px;border:1px solid ${G};background:${G};color:${N};overflow:hidden;transition:all .35s cubic-bezier(.22,1,.36,1);cursor:pointer;white-space:nowrap;text-decoration:none}
    .rbtn::before{content:'';position:absolute;inset:0;background:${N};transform:scaleX(0);transform-origin:left;transition:transform .35s cubic-bezier(.22,1,.36,1);z-index:0}
    .rbtn:hover::before{transform:scaleX(1)}
    .rbtn:hover{color:#fff;border-color:${N}}
    .rbtn span,.rbtn svg{position:relative;z-index:1}
    .gbtn{display:inline-flex;align-items:center;justify-content:center;gap:10px;font-family:'Jost',sans-serif;font-size:13px;font-weight:600;letter-spacing:0.16em;text-transform:uppercase;padding:14px 24px;border:1px solid currentColor;background:transparent;text-decoration:none;transition:all .25s;white-space:nowrap}
    .gbtn:hover{background:${G};border-color:${G};color:${N}!important}
  `}</style>
);

const SL = ({ text, light }) => (
  <div className={`sl${light ? ' sl-light' : ''}`}>
    <div className="sl-line" />
    <span className="sl-text">{text}</span>
  </div>
);

/* ─────────────────────────────────────────
   Lodge gallery — one big photo, a strip of
   thumbnails, and a full-screen viewer
   (arrows, swipe, Esc). Photos are the
   lodge's own; until it has some, the park's,
   captioned as such — never passed off as
   the lodge.
───────────────────────────────────────── */
const uniq = (xs) => [...new Set(xs.filter(Boolean))];

function Lightbox({ images, index, caption, onClose, onIndex }) {
  const touch = useRef(null);
  const go = useCallback((d) => onIndex((index + d + images.length) % images.length), [index, images.length, onIndex]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);
  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label={caption} onClick={onClose}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}>
      <img src={images[index]} alt={`${caption} (${index + 1} of ${images.length})`} onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && (
        <>
          <button type="button" className="lb-nav" style={{ left: 16 }} aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }}><ChevronLeft size={22} /></button>
          <button type="button" className="lb-nav" style={{ right: 16 }} aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }}><ChevronRight size={22} /></button>
        </>
      )}
      <div className="lb-bar fj" onClick={(e) => e.stopPropagation()}>
        <span>{caption}</span>
        <span>{index + 1} / {images.length}</span>
      </div>
      <button type="button" className="lb-close" aria-label="Close" onClick={onClose}><X size={20} /></button>
    </div>
  );
}

function LodgeGallery({ lodge, park, exclude }) {
  const own = uniq([lodge.featuredImage, ...parseList(lodge.galleryImages)]);
  // Fallback skips the photo already used as the day's banner just above.
  const images = own.length ? own : uniq([park?.featuredImage, ...parseList(park?.galleryImages)]).filter((x) => x !== exclude);
  const caption = own.length ? lodge.name : park?.name ? `${park.name} — lodge photos coming soon` : lodge.name;
  const [open, setOpen] = useState(null);
  if (!images.length) return null;
  const thumbs = images.slice(1, 4);
  const more = images.length - 1 - thumbs.length;
  return (
    <div className={`lg${thumbs.length ? ' has-thumbs' : ''}`}>
      <button type="button" className="lg-main" onClick={() => setOpen(0)} aria-label={`Open photos of ${caption}`}>
        <img src={images[0]} alt={caption} loading="lazy" />
        <span className="lg-count fj"><Images size={13} strokeWidth={1.6} /> {images.length} photo{images.length === 1 ? '' : 's'}</span>
        {!own.length && <span className="lg-note fj">Photos: {park?.name}</span>}
      </button>
      {thumbs.length > 0 && (
        <div className="lg-thumbs">
          {thumbs.map((src, k) => (
            <button key={src} type="button" className="lg-thumb" onClick={() => setOpen(k + 1)} aria-label={`Photo ${k + 2}`}>
              <img src={src} alt="" loading="lazy" />
              {k === thumbs.length - 1 && more > 0 && <span className="lg-more fc">+{more}</span>}
            </button>
          ))}
        </div>
      )}
      {open !== null && <Lightbox images={images} index={open} caption={caption} onClose={() => setOpen(null)} onIndex={setOpen} />}
    </div>
  );
}

/* ════════════════════════════════════════
   PAGE
════════════════════════════════════════ */
export default function TourItinerary() {
  const { id: slug } = useParams();
  const [activeDay, setActiveDay] = useState(0);
  const [month, setMonth] = useState(null);
  const dayRefs = useRef([]);
  const monthsRef = useRef(null);

  const { data, loading } = useQuery(GET_TOUR_ITINERARY, { variables: { slug }, fetchPolicy: 'cache-first' });
  const tour = data?.tour;

  const [trackPageView] = useMutation(TRACK_PAGE_VIEW);
  useEffect(() => {
    if (!tour?.id) return;
    trackPageView({
      variables: { pageUrl: window.location.pathname, sessionId: getVisitorSessionId(), pageTitle: tour.name, tourId: tour.id },
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.id]);

  const days = useMemo(() => tour?.itineraryDays || [], [tour]);
  const total = days.length;
  const { stops, parkZones } = useMemo(() => buildStops(days, tour?.arrivalAirportName), [days, tour]);
  const { mapStops, insets } = useMemo(() => groupDense(stops, parkZones), [stops, parkZones]);
  const monthWindow = useMemo(() => upcomingMonths(), []);
  const lensKey = month ?? defaultMonth(tour, monthWindow);
  const lensMonth = Number(lensKey.slice(5, 7));
  const lensYear = Number(lensKey.slice(0, 4));

  const hasMigrationPark = days.some((d) => MIGRATION_PARKS.has(d.primaryPark?.slug));
  const { data: migrationData } = useQuery(GREAT_MIGRATION, { skip: !hasMigrationPark, fetchPolicy: 'cache-first' });
  const herdMonthRow = (migrationData?.greatMigration || []).find((m) => m.month === lensMonth) || null;

  /* The day being read drives the map, the ribbon and the "now reading" card. */
  useEffect(() => {
    const refs = dayRefs.current;
    if (!refs.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) {
        const idx = refs.findIndex((r) => r === visible[0].target);
        if (idx !== -1) setActiveDay(idx);
      }
    }, { rootMargin: '-35% 0px -55% 0px', threshold: 0 });
    refs.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, [days]);

  /* Keep the chosen month visible in the (scrollable, on phones) month row. */
  useEffect(() => {
    const row = monthsRef.current;
    const pill = row?.querySelector('.m-pill.on');
    if (row && pill) row.scrollLeft = pill.offsetLeft - row.clientWidth / 2 + pill.clientWidth / 2;
  }, [month, tour]);

  const jumpTo = useCallback((i) => {
    setActiveDay(i);
    dayRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /* Per-day context: first day at a park (gets the photo and "look out
     for"), how the day was reached, and what the chosen month is like. */
  const chapters = useMemo(() => {
    const seen = new Set();
    return days.map((d, i) => {
      const park = d.primaryPark;
      const parkKey = park?.name || d.locationLabel || d.title;
      const firstAtPark = !seen.has(parkKey);
      seen.add(parkKey);
      const stopIdx = stops.findIndex((s) => i >= s.firstDayIdx && i <= s.lastDayIdx);
      const stop = stops[stopIdx];
      const arriving = stop && stop.firstDayIdx === i && stopIdx > 0 ? stop.transportIn : null;
      const arrivingFrom = arriving ? stops[stopIdx - 1]?.name : null;
      // A lodge is introduced once per stay; later nights just say so.
      const lodgeId = d.accommodation?.id;
      const prevLodge = i > 0 ? days[i - 1].accommodation?.id : null;
      const stayStart = lodgeId && lodgeId !== prevLodge;
      let stayNights = 0, nightOfStay = 0;
      if (lodgeId) {
        let a = i; while (a > 0 && days[a - 1].accommodation?.id === lodgeId) a -= 1;
        let b = i; while (b < days.length - 1 && days[b + 1].accommodation?.id === lodgeId) b += 1;
        stayNights = b - a + 1; nightOfStay = i - a + 1;
      }
      const arrivingTo = arriving ? stop.name : null;
      const herds = herdsForDay(herdMonthRow, park?.slug, `${d.locationLabel || ''} ${d.title || ''}`);
      const withHerds = !!herds?.here;
      return {
        firstAtPark,
        image: firstAtPark ? (park?.featuredImage || d.accommodation?.featuredImage || (i === 0 ? tour?.featuredImage : null)) : null,
        attractions: firstAtPark ? parseList(park?.mainAttractions).slice(0, 5) : [],
        arriving, arrivingFrom, arrivingTo, stayStart, stayNights, nightOfStay,
        season: seasonFor(park, lensMonth),
        herds, withHerds,
      };
    });
  }, [days, stops, herdMonthRow, lensMonth, tour]);

  if (!loading && !tour) return (
    <div className="fj" style={{ background: CR, minHeight: '100vh' }}>
      <S />
      <div className="itin-hero"><div className="itin-hero-grad" />
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 1500, margin: '0 auto', padding: '0 clamp(16px,3vw,40px)' }}>
          <SL text="Itinerary" light />
          <h1 className="fc" style={{ fontSize: 'clamp(34px,4.5vw,56px)', fontWeight: 300, color: '#fff', margin: '12px 0 16px' }}>We couldn't find that safari.</h1>
          <p className="fj" style={{ fontSize: 16, fontWeight: 300, color: 'rgba(255,255,255,.6)', marginBottom: 26 }}>It may have been renamed or retired.</p>
          <Link to="/tours" className="rbtn"><span>See all safaris</span><ArrowRight size={13} /></Link>
        </div>
      </div>
    </div>
  );

  if (loading || !tour) return (
    <div className="fj" style={{ background: CR, minHeight: '100vh' }}>
      <S />
      <div className="itin-hero"><div className="itin-hero-grad" />
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 1500, margin: '0 auto', padding: '0 20px' }}>
          <div className="skeleton" style={{ width: 160, height: 12, marginBottom: 28 }} />
          <div className="skeleton" style={{ width: 480, maxWidth: '90%', height: 52, marginBottom: 14 }} />
          <div className="skeleton" style={{ width: 320, maxWidth: '70%', height: 14 }} />
        </div>
      </div>
    </div>
  );

  const countries = [...new Set((tour.parks || []).map((p) => p.country?.name).filter(Boolean))];
  const parkCount = new Set(days.map((d) => d.primaryPark?.name).filter(Boolean)).size;
  const departures = (tour.upcomingDepartures || []).filter((d) => d.departureDate).slice(0, 4);
  const depMonths = new Set(departures.map((d) => monthKeyOf(d.departureDate)));
  const lastStop = stops[stops.length - 1];
  const activeChapter = days[activeDay];
  const mapSize = mapCanvasSize(mapStops.length);

  return (
    <div className="fj itin-page" style={{ background: CR, minHeight: '100vh' }}>
      <S />

      {/* ══ HERO ══ */}
      <header className="itin-hero">
        <img src={tour.featuredImage} alt="" className="itin-hero-img fi" />
        <div className="itin-hero-grad" />
        <div className="itin-hero-grain" />
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 1500, margin: '0 auto', padding: '0 clamp(16px,3vw,40px)' }}>
          <Link to={`/tours/${slug}`} className="fj"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 26, fontSize: 13, fontWeight: 600, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.45)', textDecoration: 'none' }}>
            <ArrowLeft size={12} strokeWidth={1.5} /> Safari overview
          </Link>
          <div className="ri"><SL text="Your safari, day by day" light /></div>
          <h1 className="fc ri d1" style={{ fontSize: 'clamp(40px,5.5vw,72px)', fontWeight: 300, color: '#fff', lineHeight: .95, margin: '12px 0 16px', maxWidth: 820 }}>
            {tour.name}
          </h1>
          {tour.tagline && (
            <p className="fj ri d2" style={{ fontSize: 16, fontWeight: 300, color: 'rgba(255,255,255,.6)', marginBottom: 30, maxWidth: 560, lineHeight: 1.7 }}>
              {tour.tagline}
            </p>
          )}
          <div className="ri d3" style={{ display: 'flex', flexWrap: 'wrap', rowGap: 18 }}>
            {[
              [tour.durationLabel, 'On safari'],
              [parkCount ? `${parkCount} ${parkCount === 1 ? 'park' : 'parks'}` : null, countries.join(' · ') || 'East Africa'],
              [tour.arrivalAirportName ? 'Fly in' : null, tour.arrivalAirportName],
              [lastStop ? 'Ends in' : null, lastStop?.name],
              [tour.recommendedBestSeason ? 'Best' : null, tour.recommendedBestSeason],
              [`Max ${tour.maxParticipants}`, 'Guests per vehicle'],
            ].filter(([v, l]) => v && l).map(([v, l]) => (
              <div key={l} className="hero-fact">
                <span className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(184,151,90,.85)' }}>{v}</span>
                <span className="fc" style={{ fontSize: 21, fontWeight: 400, color: '#fff', lineHeight: 1.15 }}>{l}</span>
              </div>
            ))}
          </div>

          {/* Journey ribbon */}
          {mapStops.filter((s) => !s.isArrival).length > 1 && (
            <nav className="ribbon ri d4" aria-label="Route overview">
              {mapStops.filter((s) => !s.isArrival).map((s) => {
                const n = s.dayTo - s.dayFrom + 1;
                const on = activeDay >= s.firstDayIdx && activeDay <= s.lastDayIdx;
                return (
                  <button key={s.key} type="button" className={`ribbon-seg${on ? ' on' : ''}`}
                    style={{ flex: `${n} 1 0` }} onClick={() => jumpTo(s.firstDayIdx)}>
                    <span className="fj" style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '.16em', textTransform: 'uppercase', color: G }}>
                      {s.dayFrom === s.dayTo ? `Day ${s.dayFrom}` : `Days ${s.dayFrom}–${s.dayTo}`}
                    </span>
                    <span className="fc" style={{ display: 'block', fontSize: 18, marginTop: 4, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      {/* ══ ROUTE — full width, as big as the trip needs (mapCanvasSize) ══ */}
      {stops.length >= 1 && (
        <section style={{ background: '#faf8f4', borderBottom: '1px solid #ece7de' }}>
          <div style={{ maxWidth: `${Math.max(1500, mapSize.w + 260)}px`, margin: '0 auto', padding: '40px clamp(16px,3vw,40px) 44px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Navigation size={14} strokeWidth={1.5} style={{ color: G }} />
                <span className="fj" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.16em', textTransform: 'uppercase', color: N }}>
                  Your route · {mapStops.filter((x) => !x.isArrival).length} stops · {total} days
                </span>
              </div>
              <span className="fj" style={{ fontSize: 12.5, color: '#7a7266' }}>Roads drawn as driven · tap a stop to read that day</span>
            </div>
            <div style={{ maxWidth: `${mapSize.w + 220}px`, margin: '0 auto' }}>
              <Suspense fallback={<div style={{ aspectRatio: '1280 / 820' }} />}>
                <RouteMap stops={mapStops} parkZones={parkZones} activeStop={activeDay} onJump={jumpTo} insets={insets} />
              </Suspense>
            </div>
          </div>
        </section>
      )}

      {/* ══ MONTH LENS ══ */}
      <div className="lens">
        <div className="lens-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <CalendarDays size={16} strokeWidth={1.5} style={{ color: G }} />
            <span className="fj" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: N }}>When are you travelling?</span>
          </div>
          <div className="lens-months" ref={monthsRef} role="radiogroup" aria-label="Travel month">
            {monthWindow.map(({ y, m, key }, k) => (
              <button key={key} type="button" role="radio" aria-checked={lensKey === key}
                className={`m-pill${lensKey === key ? ' on' : ''}`} onClick={() => setMonth(key)}
                title={`${MONTHS_LONG[m - 1]} ${y}${depMonths.has(key) ? ' — scheduled departures' : ''}`}>
                {MONTHS[m - 1]}{(k === 0 || m === 1) && <span className="m-yr"> {String(y).slice(2)}</span>}
                {depMonths.has(key) && <span className="dep" />}
              </button>
            ))}
          </div>
          <span className="fj lens-hint" style={{ fontSize: 12.5, color: '#7a7266' }}>
            Each day shows what {MONTHS_LONG[lensMonth - 1]} {lensYear} is like there.
          </span>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="itin-layout">
        <main>
          {tour.pullQuote && (
            <blockquote className="fc" style={{ fontSize: 'clamp(22px,2.4vw,28px)', fontWeight: 300, color: N, lineHeight: 1.35, margin: '0 0 30px', paddingLeft: 20, borderLeft: `2px solid ${G}` }}>
              “{tour.pullQuote}”
              {tour.pullQuoteBy && <span className="fj" style={{ display: 'block', fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: G, marginTop: 10 }}>— {tour.pullQuoteBy}</span>}
            </blockquote>
          )}

          {days.map((day, i) => {
            const c = chapters[i] || {};
            const on = activeDay === i;
            const T = c.arriving ? TRANSPORT[c.arriving] : null;
            const drive = day.driveFromPreviousKm ? Number(day.driveFromPreviousKm) : null;
            const facts = [
              drive ? { I: Car, t: `${Math.round(drive)} km · ${driveTime(drive)} on the road` } : null,
              day.primaryPark?.elevationM && c.firstAtPark && !/elevation/i.test(day.locationLabel || '') ? { I: Mountain, t: `${Number(day.primaryPark.elevationM).toLocaleString()} m above sea level` } : null,
              day.boardBasis?.name ? { I: Utensils, t: day.boardBasis.name } : null,
              day.craterDescent ? { I: Sun, t: 'Crater descent today' } : null,
              day.isOvernight && !day.accommodation
                ? { I: Building2, t: stops.find((x) => i >= x.firstDayIdx && i <= x.lastDayIdx)?.isTrek ? 'Mountain camp (tents)' : 'Overnight on safari' }
                : null,
            ].filter(Boolean);
            const place = day.locationLabel || day.primaryPark?.name || day.location?.name || '';

            return (
              <React.Fragment key={day.id}>
                {T && (
                  <div className="transit">
                    <span className="transit-line" />
                    <T.I size={14} strokeWidth={1.5} style={{ color: G }} />
                    <span>{T.verb} from {c.arrivingFrom} to {c.arrivingTo}</span>
                  </div>
                )}
                <article ref={(el) => { dayRefs.current[i] = el; }} className={`chapter${on ? ' on' : ''}`} aria-labelledby={`day-${day.dayNumber}`}>
                  {c.image ? (
                    <div className="ch-banner">
                      <img src={c.image} alt={place} loading={i < 2 ? 'eager' : 'lazy'}
                        onError={(e) => {
                          // A dead park photo would leave an empty dark band —
                          // fall back to the safari's own cover once.
                          if (tour.featuredImage && e.currentTarget.src !== tour.featuredImage) e.currentTarget.src = tour.featuredImage;
                        }} />
                      <div className="ch-banner-text">
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                          <span className="ch-num" style={{ fontSize: 'clamp(64px,7vw,92px)' }}>{pad2(day.dayNumber)}</span>
                          <div style={{ paddingBottom: 6, minWidth: 0 }}>
                            <span className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,.7)' }}>
                              {day.timeLabel || `Day ${day.dayNumber}`}{day.durationLabel ? ` · ${day.durationLabel}` : ''}
                            </span>
                            <h2 id={`day-${day.dayNumber}`} className="fc" style={{ fontSize: 'clamp(26px,3vw,36px)', fontWeight: 400, lineHeight: 1.05, margin: '4px 0 0' }}>{day.title}</h2>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ch-head">
                      <span className="ch-num" style={{ fontSize: 56 }}>{pad2(day.dayNumber)}</span>
                      <div style={{ minWidth: 0 }}>
                        <span className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.2em', textTransform: 'uppercase', color: G }}>
                          {day.timeLabel || `Day ${day.dayNumber}`}{day.durationLabel ? ` · ${day.durationLabel}` : ''}
                        </span>
                        <h2 id={`day-${day.dayNumber}`} className="fc" style={{ fontSize: 'clamp(24px,2.6vw,30px)', fontWeight: 400, color: N, lineHeight: 1.1, margin: '4px 0 0' }}>{day.title}</h2>
                      </div>
                    </div>
                  )}

                  <div className="ch-body">
                    {(place || facts.length > 0) && (
                      <div className="chips" style={{ marginBottom: 16 }}>
                        {place && <span className="chip"><MapPin size={12} strokeWidth={1.5} style={{ color: G }} />{place}</span>}
                        {facts.map(({ I, t }) => <span key={t} className="chip"><I size={12} strokeWidth={1.5} style={{ color: G }} />{t}</span>)}
                      </div>
                    )}

                    {day.description && (
                      <p className="fj" style={{ fontSize: 16, fontWeight: 300, lineHeight: 1.85, color: '#12100e', margin: '0 0 20px' }}>{day.description}</p>
                    )}

                    <div style={{ display: 'grid', gap: 22 }}>
                      {day.details?.length > 0 && (
                        <div>
                          <p className="label">How the day unfolds</p>
                          <div className="flow">
                            {day.details.map((d) => <div key={d} className="flow-step">{d}</div>)}
                          </div>
                        </div>
                      )}

                      {c.attractions.length > 0 && (
                        <div>
                          <p className="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Eye size={13} strokeWidth={1.6} style={{ color: G }} /> Look out for</p>
                          <div className="lookout">{c.attractions.map((a) => <span key={a}>{a}</span>)}</div>
                        </div>
                      )}

                      {(c.season || c.herds) && (
                        <div className="season-box">
                          <CalendarDays size={16} strokeWidth={1.5} style={{ color: c.season?.tone || G, marginTop: 2, flexShrink: 0 }} />
                          <div className="fj" style={{ fontSize: 14, lineHeight: 1.6, color: '#12100e' }}>
                            {c.season && (
                              <p style={{ margin: 0 }}>
                                <strong style={{ fontWeight: 600, color: c.season.tone }}>{c.season.label}</strong> in {MONTHS_LONG[lensMonth - 1]}
                                {day.primaryPark?.name ? ` at ${day.primaryPark.name.replace(/ National Park| Conservation Area/, '')}` : ''} — {c.season.note}
                              </p>
                            )}
                            {c.herds && c.firstAtPark && (
                              <p style={{ margin: c.season ? '6px 0 0' : 0 }}>
                                <strong style={{ fontWeight: 600 }}>The Great Migration:</strong>{' '}
                                {c.withHerds
                                  ? `in ${MONTHS_LONG[lensMonth - 1]} you're where the herds usually are, around ${c.herds.where}: ${c.herds.moment}.`
                                  : `in ${MONTHS_LONG[lensMonth - 1]} the herds are usually around ${c.herds.where} (${c.herds.moment}) — ask us about routing via that area.`}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {day.includesAtStop?.length > 0 && (
                        <div>
                          <p className="label">Included today</p>
                          <div className="chips">
                            {day.includesAtStop.map((inc) => <span key={inc} className="chip" style={{ background: '#fff' }}><Check size={11} strokeWidth={2.4} style={{ color: G }} />{inc}</span>)}
                          </div>
                        </div>
                      )}

                      {day.insiderTip && (
                        <div className="guide-note">
                          <p className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.2em', textTransform: 'uppercase', color: G, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 7 }}>
                            <Sparkles size={12} strokeWidth={1.6} /> From your guide
                          </p>
                          <p className="fc" style={{ fontSize: 20, fontStyle: 'italic', fontWeight: 400, color: '#2a241c', lineHeight: 1.5, margin: 0 }}>“{day.insiderTip}”</p>
                        </div>
                      )}

                      {day.accommodation && !c.stayStart && (
                        <div className="chip" style={{ alignSelf: 'flex-start', background: '#fff' }}>
                          <Building2 size={12} strokeWidth={1.5} style={{ color: G }} />
                          Night {c.nightOfStay} of {c.stayNights} at {day.accommodation.name}
                        </div>
                      )}

                      {day.accommodation && c.stayStart && (() => {
                        const a = day.accommodation;
                        const locLbl = LOC_TYPE_LABELS[a.locationType] || (a.locationType || '').replace(/_/g, ' ');
                        const rating = Number(a.averageRating || 0);
                        const meta = [a.levelLabel, a.propertyType?.name, locLbl, day.roomType?.name].filter(Boolean);
                        return (
                          <div className="stay">
                            <div className="stay-head">
                              <p className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.22em', textTransform: 'uppercase', color: G, margin: '0 0 4px' }}>
                                Where you'll sleep{c.stayNights > 1 ? ` · ${c.stayNights} nights` : ''}
                              </p>
                              <p className="fc" style={{ fontSize: 26, fontWeight: 600, color: '#12100e', margin: '0 0 4px', lineHeight: 1.15 }}>{a.name}</p>
                              <p className="fj" style={{ fontSize: 13, color: '#6b6357', margin: '0 0 8px' }}>
                                {meta.join(' · ')}{rating > 0 && <> · <Star size={11} strokeWidth={0} fill={G} style={{ verticalAlign: '-1px' }} /> {rating.toFixed(1)}</>}
                              </p>
                              {(a.shortDescription || a.description) && (
                                <p className="fj" style={{ fontSize: 14.5, fontWeight: 300, lineHeight: 1.7, color: '#12100e', margin: 0 }}>{a.shortDescription || a.description}</p>
                              )}
                            </div>
                            <LodgeGallery lodge={a} park={day.primaryPark} exclude={c.image} />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </article>
              </React.Fragment>
            );
          })}

          {/* ══ WHAT'S INCLUDED ══ */}
          <section style={{ marginTop: 48 }}>
            <SL text="What your safari price covers" />
            <h2 className="fc" style={{ fontSize: 'clamp(28px,3vw,38px)', fontWeight: 300, color: N, margin: '6px 0 22px' }}>No surprises at the gate</h2>
            <div className="incl-grid">
              <div className="card" style={{ padding: '18px 22px', borderTop: `2px solid ${G}` }}>
                <p className="label" style={{ color: G }}>Included</p>
                {(tour.includes || []).map((item) => (
                  <div key={item} className="incl-item"><Check size={14} strokeWidth={2.2} style={{ color: G, marginTop: 4, flexShrink: 0 }} />{item}</div>
                ))}
              </div>
              <div className="card" style={{ padding: '18px 22px', borderTop: '2px solid #d6cfc3' }}>
                <p className="label">Not included</p>
                {(tour.excludes || []).map((item) => (
                  <div key={item} className="incl-item"><X size={14} strokeWidth={2.2} style={{ color: '#b45a4a', marginTop: 4, flexShrink: 0 }} />{item}</div>
                ))}
                <p className="fj" style={{ fontSize: 13.5, fontWeight: 300, color: '#6b6357', lineHeight: 1.7, margin: '12px 0 0' }}>
                  We recommend travel insurance that covers medical evacuation — our team can point you to trusted providers.
                </p>
              </div>
            </div>

            {(tour.additionalCosts || []).length > 0 && (
              <div style={{ marginTop: 28 }}>
                <p className="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={13} strokeWidth={1.6} style={{ color: G }} /> Extras & add-ons</p>
                <div className="addl-grid">
                  {tour.additionalCosts.map((item) => {
                    const ct = COST_TYPE_MAP[item.costType] || COST_TYPE_MAP.optional;
                    return (
                      <div key={item.id} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
                          <span className="fj" style={{ fontSize: 15, fontWeight: 500, color: '#12100e' }}>{item.name}</span>
                          <span className={`addl-badge ${ct.cls}`}><Plus size={8} strokeWidth={2.5} />{ct.label}</span>
                        </div>
                        {item.description && <p className="fj" style={{ fontSize: 13.5, fontWeight: 300, color: '#12100e', lineHeight: 1.6, margin: 0 }}>{item.description}</p>}
                        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                          <span className="fc" style={{ fontSize: 22, fontWeight: 400, color: N }}>{item.priceDisplay}</span>
                          <span className="fj" style={{ fontSize: 13, color: '#6b6357' }}>{item.pricePer}</span>
                        </div>
                        {item.note && <p className="fj" style={{ fontSize: 12.5, color: '#6b6357', margin: 0 }}>{item.note}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* ══ MAKE IT YOURS ══ */}
          <section style={{ marginTop: 48, background: N, color: '#fff', padding: 'clamp(28px,4vw,48px)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: .5, pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              <SL text="Make it yours" light />
              <h2 className="fc" style={{ fontSize: 'clamp(28px,3.4vw,44px)', fontWeight: 300, lineHeight: 1.1, margin: '6px 0 12px', maxWidth: 640 }}>
                Every safari we run can be adjusted — more nights, a different camp, a balloon flight at dawn.
              </h2>
              <p className="fj" style={{ fontSize: 15.5, fontWeight: 300, color: 'rgba(255,255,255,.65)', maxWidth: 560, lineHeight: 1.7, margin: '0 0 24px' }}>
                Book it as it is, or tell us what you'd change and a safari specialist will come back with a tailored plan and price.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to={`/booking/${slug}`} className="rbtn"><span>Reserve this safari</span><ArrowRight size={13} /></Link>
                <Link to="/tour-builder" state={{ fromTour: { slug: tour.slug, name: tour.name, nights: tour.durationNights ?? Math.max(0, (tour.duration || 1) - 1) } }}
                  className="gbtn" style={{ color: '#fff' }}>Customise this safari</Link>
                <Link to="/contact" className="gbtn" style={{ color: 'rgba(255,255,255,.8)' }}><MessageCircle size={13} /> Talk to a specialist</Link>
              </div>
            </div>
          </section>
        </main>

        {/* ══ STICKY COMPANION ══ */}
        <aside className="aside" aria-label="Route and booking">
          {activeChapter && (
            <div className="now">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span className="fj" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.2em', textTransform: 'uppercase', color: G }}>Day {activeChapter.dayNumber} of {total}</span>
                <span className="fj" style={{ fontSize: 12, color: 'rgba(255,255,255,.45)' }}>{activeChapter.primaryPark?.name || activeChapter.locationLabel}</span>
              </div>
              <p className="fc" style={{ fontSize: 21, lineHeight: 1.2, margin: '6px 0 12px' }}>{activeChapter.title}</p>
              <div style={{ height: 2, background: 'rgba(255,255,255,.1)' }}>
                <div style={{ height: 2, width: `${((activeDay + 1) / Math.max(total, 1)) * 100}%`, background: G, transition: 'width .5s ease' }} />
              </div>
            </div>
          )}

          {departures.length > 0 && (
            <div className="card" style={{ padding: '14px 18px' }}>
              <p className="label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Clock size={13} strokeWidth={1.6} style={{ color: G }} /> Next departures
              </p>
              {departures.map((d) => {
                const m = monthKeyOf(d.departureDate);
                return (
                  <button key={d.id} type="button" className="dep-row" onClick={() => setMonth(m)} title="Show this month on the itinerary">
                    <span className="fj" style={{ fontSize: 14, color: lensKey === m ? G : '#12100e', fontWeight: lensKey === m ? 600 : 400 }}>
                      {fmtDate(d.departureDate)}{d.returnDate ? ` – ${fmtDate(d.returnDate)}` : ''}
                    </span>
                    <span className="fj" style={{ fontSize: 12.5, color: d.isFull ? '#b45a4a' : '#5f8a5a' }}>
                      {d.isFull ? 'Full' : d.spotsRemaining != null ? `${d.spotsRemaining} seats left` : 'Available'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <Link to={`/booking/${slug}`} className="rbtn" style={{ width: '100%' }}><span>Reserve this safari</span><ArrowRight size={13} /></Link>
          <Link to="/contact" className="gbtn" style={{ color: N, width: '100%' }}><MessageCircle size={13} /> Ask a safari specialist</Link>
          <p className="fj" style={{ fontSize: 12.5, color: '#7a7266', textAlign: 'center', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Users size={12} strokeWidth={1.5} /> Private vehicle · max {tour.maxParticipants} guests
          </p>
        </aside>
      </div>

      {activeChapter && (
        <div className="mbar">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="fj" style={{ display: 'block', fontSize: 10.5, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: G }}>Day {activeChapter.dayNumber} of {total}</span>
            <span className="fc" style={{ display: 'block', fontSize: 17, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeChapter.title}</span>
          </div>
          <Link to={`/booking/${slug}`} className="rbtn" style={{ padding: '11px 16px', fontSize: 12 }}><span>Reserve</span><ArrowRight size={12} /></Link>
        </div>
      )}
    </div>
  );
}
